import React, { useState, useEffect } from 'react';
import { ceremonyAPI } from '../services/api';
import { Search, Filter, Users, Calendar, MapPin, Sparkles, Heart, Star, Clock, Feather } from 'lucide-react';

const RitesAndCeremonies = () => {
  const [ceremonies, setCeremonies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedTribe, setSelectedTribe] = useState('');
  const [selectedCeremony, setSelectedCeremony] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const ceremonyTypes = [
    { value: 'spiritual', label: 'Spiritual Ceremonies', icon: Sparkles },
    { value: 'community', label: 'Community Ceremonies', icon: Users },
    { value: 'seasonal', label: 'Seasonal Ceremonies', icon: Calendar },
    { value: 'healing', label: 'Healing Ceremonies', icon: Feather },
    { value: 'death', label: 'Death & Mourning', icon: Heart },
    { value: 'puberty', label: 'Puberty Ceremonies', icon: Star },
    { value: 'marriage', label: 'Marriage Ceremonies', icon: Heart }
  ];

  useEffect(() => {
    fetchCeremonies();
  }, [selectedType, selectedTribe, searchQuery]);

  const fetchCeremonies = async () => {
    try {
      setLoading(true);
      const searchParams = {};
      if (searchQuery) searchParams.query = searchQuery;
      if (selectedType) searchParams.ceremony_type = selectedType;
      if (selectedTribe) searchParams.tribe = selectedTribe;

      const data = await ceremonyAPI.searchCeremonies(searchParams);
      setCeremonies(data || []);
    } catch (error) {
      console.error('Error fetching ceremonies:', error);
      setCeremonies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCeremonyClick = async (ceremony) => {
    try {
      const details = await ceremonyAPI.getCeremonyDetails(ceremony.id);
      setSelectedCeremony(details);
    } catch (error) {
      console.error('Error fetching ceremony details:', error);
      // Fallback to basic ceremony data
      setSelectedCeremony(ceremony);
    }
  };

  const getCeremonyTypeIcon = (type) => {
    const ceremonyType = ceremonyTypes.find(ct => ct.value === type);
    return ceremonyType ? ceremonyType.icon : Sparkles;
  };

  const getCurrentPracticeColor = (practice) => {
    switch (practice) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'modified': return 'bg-yellow-100 text-yellow-800';
      case 'rare': return 'bg-orange-100 text-orange-800';
      case 'documented_only': return 'bg-gray-100 text-gray-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  if (selectedCeremony) {
    const IconComponent = getCeremonyTypeIcon(selectedCeremony.ceremony_type);
    
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setSelectedCeremony(null)}
            className="flex items-center text-blue-600 hover:text-blue-800"
          >
            ← Back to Ceremonies
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="flex items-start space-x-4 mb-6">
            <div className="p-3 bg-blue-100 rounded-lg">
              <IconComponent className="w-8 h-8 text-blue-600" />
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{selectedCeremony.name}</h1>
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <span className="flex items-center">
                  <MapPin className="w-4 h-4 mr-1" />
                  {selectedCeremony.tribe_or_region}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCurrentPracticeColor(selectedCeremony.current_practice)}`}>
                  {selectedCeremony.current_practice?.replace('_', ' ')}
                </span>
                {selectedCeremony.duration && (
                  <span className="flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    {selectedCeremony.duration}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Overview</h3>
                <p className="text-gray-700 leading-relaxed">{selectedCeremony.overview}</p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Purpose</h3>
                <p className="text-gray-700 leading-relaxed">{selectedCeremony.purpose}</p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Spiritual Significance</h3>
                <p className="text-gray-700 leading-relaxed">{selectedCeremony.spiritual_significance}</p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Cultural Meaning</h3>
                <p className="text-gray-700 leading-relaxed">{selectedCeremony.cultural_meaning}</p>
              </div>
            </div>

            <div className="space-y-6">
              {selectedCeremony.participants && selectedCeremony.participants.length > 0 && (
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Participants</h3>
                  <div className="space-y-2">
                    {selectedCeremony.participants.map((participant, index) => (
                      <div key={index} className="bg-gray-50 p-3 rounded-lg">
                        <div className="font-medium text-gray-900">{participant.role}</div>
                        {participant.requirements && (
                          <div className="text-sm text-gray-600">{participant.requirements}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedCeremony.ceremonial_items && selectedCeremony.ceremonial_items.length > 0 && (
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Sacred Items</h3>
                  <div className="space-y-2">
                    {selectedCeremony.ceremonial_items.map((item, index) => (
                      <div key={index} className="bg-gray-50 p-3 rounded-lg">
                        <div className="font-medium text-gray-900">{item.name}</div>
                        <div className="text-sm text-gray-600">{item.purpose}</div>
                        {item.symbolism && (
                          <div className="text-xs text-gray-500 italic">{item.symbolism}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedCeremony.preparation_required && selectedCeremony.preparation_required.length > 0 && (
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Preparation Required</h3>
                  <ul className="space-y-1">
                    {selectedCeremony.preparation_required.map((prep, index) => (
                      <li key={index} className="text-gray-700 flex items-start">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                        {prep}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedCeremony.modern_adaptations && (
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Modern Adaptations</h3>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-gray-700 mb-3">{selectedCeremony.modern_adaptations.description}</p>
                    {selectedCeremony.modern_adaptations.changes_made && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Changes Made:</h4>
                        <ul className="space-y-1">
                          {selectedCeremony.modern_adaptations.changes_made.map((change, index) => (
                            <li key={index} className="text-sm text-gray-600 flex items-start">
                              <span className="w-1 h-1 bg-blue-400 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                              {change}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {selectedCeremony.phases && selectedCeremony.phases.length > 0 && (
            <div className="mt-8 border-t pt-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Ceremony Phases</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {selectedCeremony.phases.map((phase, index) => (
                  <div key={index} className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                    <div className="flex items-center mb-2">
                      <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-2">
                        {index + 1}
                      </span>
                      <h4 className="font-medium text-gray-900">{phase.name}</h4>
                    </div>
                    {phase.duration && (
                      <div className="text-xs text-gray-600 mb-2">Duration: {phase.duration}</div>
                    )}
                    <p className="text-sm text-gray-700 mb-2">{phase.description}</p>
                    {phase.activities && (
                      <div className="space-y-1">
                        {phase.activities.map((activity, actIndex) => (
                          <div key={actIndex} className="text-xs text-gray-600 flex items-center">
                            <span className="w-1 h-1 bg-blue-400 rounded-full mr-1"></span>
                            {activity}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Rites & Ceremonies</h2>
          <p className="text-gray-600 mt-1">Explore sacred tribal ceremonies and traditional rites of passage</p>
        </div>
        
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Filter className="w-4 h-4 mr-2" />
          Filters
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search ceremonies, tribes, or practices..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ceremony Type</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                >
                  <option value="">All Types</option>
                  {ceremonyTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tribe/Region</label>
                <input
                  type="text"
                  placeholder="e.g., Cherokee, Ojibwe, Hopi..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={selectedTribe}
                  onChange={(e) => setSelectedTribe(e.target.value)}
                />
              </div>

              <div className="flex items-end">
                <button
                  onClick={() => {
                    setSelectedType('');
                    setSelectedTribe('');
                    setSearchQuery('');
                  }}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Ceremony Type Categories */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
        {ceremonyTypes.map(type => {
          const IconComponent = type.icon;
          const isSelected = selectedType === type.value;
          
          return (
            <button
              key={type.value}
              onClick={() => setSelectedType(isSelected ? '' : type.value)}
              className={`p-4 rounded-lg text-center transition-colors ${
                isSelected
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-blue-50 shadow'
              }`}
            >
              <IconComponent className={`w-8 h-8 mx-auto mb-2 ${isSelected ? 'text-white' : 'text-blue-600'}`} />
              <div className="text-xs font-medium">{type.label.replace(' Ceremonies', '')}</div>
            </button>
          );
        })}
      </div>

      {/* Ceremonies Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-lg shadow p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded mb-4"></div>
              <div className="h-3 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 bg-gray-200 rounded mb-4"></div>
              <div className="h-2 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {ceremonies.map(ceremony => {
            const IconComponent = getCeremonyTypeIcon(ceremony.ceremony_type);
            
            return (
              <div
                key={ceremony.id}
                className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer p-6"
                onClick={() => handleCeremonyClick(ceremony)}
              >
                <div className="flex items-start space-x-3 mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <IconComponent className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 line-clamp-2">{ceremony.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">{ceremony.tribe_or_region}</p>
                  </div>
                </div>
                
                <p className="text-gray-700 text-sm mb-4 line-clamp-3">{ceremony.overview}</p>
                
                <div className="flex items-center justify-between">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCurrentPracticeColor(ceremony.current_practice)}`}>
                    {ceremony.current_practice?.replace('_', ' ')}
                  </span>
                  {ceremony.duration && (
                    <span className="text-xs text-gray-500 flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      {ceremony.duration}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && ceremonies.length === 0 && (
        <div className="text-center py-12">
          <Sparkles className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No ceremonies found</h3>
          <p className="text-gray-600">Try adjusting your search terms or filters</p>
        </div>
      )}
    </div>
  );
};

export default RitesAndCeremonies;